import { z } from "zod";
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema, SourceRefSchema } from "./source.js";
import { GbpMoneySchema, QuoteProductGroupSchema, QuoteCurrencySchema } from "./quoting.js";
export const MoneyMismatchClassificationSchema = z.enum([
    "price_list_drift",
    "quantity_delta",
    "scope_difference",
    "missing_price",
    "legacy_sku_mapping",
    "commercial_override",
    "source_data_gap",
    "golden_error",
    "unexplained"
]);
export const MoneyMismatchStatusSchema = z.enum([
    "unexplained",
    "classified",
    "blocked_by_accuracy_delta",
    "closed_fixed",
    "closed_adjudicated",
    "closed_accepted"
]);
export const TERMINAL_MONEY_MISMATCH_STATUSES = [
    "closed_fixed",
    "closed_adjudicated",
    "closed_accepted"
];
export const MoneyEvidenceKindSchema = z.enum([
    "generated_quote_row",
    "delivered_quote_row",
    "price_source",
    "quote_pricing_issue",
    "d1_scope_policy",
    "catalog_alias",
    "rich_commercial_decision",
    "source_index_row",
    "golden_correction",
    "agent_trace",
    "accuracy_mismatch_ref"
]);
export const AccuracyMismatchStatusSchema = z.enum([
    "unexplained",
    "open",
    "fix_dispatched",
    "closed_fixed",
    "closed_adjudicated",
    "closed_accepted",
    "reverted"
]);
export const TERMINAL_ACCURACY_MISMATCH_STATUSES = [
    "closed_fixed",
    "closed_adjudicated",
    "closed_accepted"
];
export const AccuracyMismatchRefSchema = z
    .object({
    accuracyMismatchId: IdSchema,
    accuracyLedgerVersion: z.number().int().nonnegative(),
    accuracyLedgerHash: NonEmptyStringSchema,
    accuracyStatus: AccuracyMismatchStatusSchema,
    accuracyClassification: NonEmptyStringSchema
})
    .strict();
export const MoneyEvidenceSchema = z
    .object({
    kind: MoneyEvidenceKindSchema,
    sourceRefs: z.array(SourceRefSchema).min(1),
    note: NonEmptyStringSchema.optional(),
    accuracyMismatchRef: AccuracyMismatchRefSchema.optional()
})
    .strict()
    .superRefine((evidence, ctx) => {
    if (evidence.kind === "accuracy_mismatch_ref" && !evidence.accuracyMismatchRef) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "accuracy_mismatch_ref evidence requires accuracyMismatchRef"
        });
    }
    if (evidence.kind !== "accuracy_mismatch_ref" && evidence.accuracyMismatchRef) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "accuracyMismatchRef is only valid on accuracy_mismatch_ref evidence"
        });
    }
});
const isTerminalMoneyStatus = (status) => TERMINAL_MONEY_MISMATCH_STATUSES.includes(status);
const isTerminalAccuracyStatus = (status) => TERMINAL_ACCURACY_MISMATCH_STATUSES.includes(status);
const requiredEvidenceKindsByClassification = {
    price_list_drift: ["generated_quote_row", "delivered_quote_row", "price_source"],
    quantity_delta: [
        "generated_quote_row",
        "delivered_quote_row",
        "accuracy_mismatch_ref"
    ],
    scope_difference: ["generated_quote_row", "delivered_quote_row", "d1_scope_policy"],
    missing_price: ["quote_pricing_issue", "generated_quote_row"],
    legacy_sku_mapping: ["catalog_alias", "generated_quote_row", "delivered_quote_row"],
    commercial_override: ["delivered_quote_row", "rich_commercial_decision"],
    source_data_gap: ["source_index_row", "agent_trace"],
    golden_error: ["delivered_quote_row", "golden_correction"],
    unexplained: ["agent_trace"]
};
const terminalStatusesByClassification = {
    price_list_drift: ["closed_adjudicated", "closed_accepted"],
    quantity_delta: ["closed_fixed", "closed_adjudicated", "closed_accepted"],
    scope_difference: ["closed_adjudicated", "closed_accepted"],
    missing_price: ["closed_fixed", "closed_accepted"],
    legacy_sku_mapping: ["closed_fixed", "closed_accepted"],
    commercial_override: ["closed_adjudicated", "closed_accepted"],
    source_data_gap: ["closed_fixed", "closed_accepted"],
    golden_error: ["closed_fixed"]
};
export const MoneyMismatchSchema = z
    .object({
    id: IdSchema,
    benchmarkCaseId: IdSchema,
    classification: MoneyMismatchClassificationSchema,
    status: MoneyMismatchStatusSchema,
    amountContribution: GbpMoneySchema,
    material: z.boolean(),
    generatedQuoteRowIds: z.array(IdSchema),
    deliveredQuoteRowRefs: z.array(SourceRefSchema),
    evidence: z.array(MoneyEvidenceSchema),
    classifiedBy: NonEmptyStringSchema.optional(),
    closedBy: NonEmptyStringSchema.optional(),
    ledgerRef: NonEmptyStringSchema.optional(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    closedAt: IsoTimestampSchema.optional(),
    reviewedBy: NonEmptyStringSchema.optional()
})
    .strict()
    .superRefine((mismatch, ctx) => {
    const evidenceKinds = mismatch.evidence.map((evidence) => evidence.kind);
    const hasEvidence = (kind) => evidenceKinds.includes(kind);
    const req = (condition, message) => {
        if (!condition) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message });
        }
    };
    if (mismatch.classification === "unexplained") {
        req(mismatch.status === "unexplained", "classification unexplained requires status unexplained");
    }
    else {
        req(mismatch.status !== "unexplained", "classified money mismatch may not keep status unexplained");
        req(!!mismatch.classifiedBy, "classified money mismatch requires classifiedBy");
    }
    for (const kind of requiredEvidenceKindsByClassification[mismatch.classification]) {
        req(hasEvidence(kind), `${mismatch.classification} requires ${kind} evidence`);
    }
    if (mismatch.classification !== "quantity_delta") {
        req(!hasEvidence("accuracy_mismatch_ref"), "accuracy_mismatch_ref evidence is only valid for quantity_delta");
    }
    if (mismatch.classification === "quantity_delta") {
        const accuracyRefs = mismatch.evidence.flatMap((evidence) => evidence.accuracyMismatchRef ? [evidence.accuracyMismatchRef] : []);
        req(accuracyRefs.length === 1, "quantity_delta requires exactly one accuracy_mismatch_ref");
        const accuracyRef = accuracyRefs[0];
        if (accuracyRef !== undefined) {
            const accuracyTerminal = isTerminalAccuracyStatus(accuracyRef.accuracyStatus);
            if (!accuracyTerminal) {
                req(mismatch.status === "blocked_by_accuracy_delta", "quantity_delta with non-terminal accuracy mismatch must be blocked_by_accuracy_delta");
            }
            else {
                req(mismatch.status !== "blocked_by_accuracy_delta", "quantity_delta may not stay blocked after the referenced accuracy mismatch is terminal");
            }
            if (isTerminalMoneyStatus(mismatch.status)) {
                req(accuracyTerminal, "quantity_delta cannot close before the referenced accuracy mismatch is terminal");
            }
        }
    }
    if (isTerminalMoneyStatus(mismatch.status)) {
        req(!!mismatch.closedBy, "terminal money mismatch requires closedBy");
        req(!!mismatch.closedAt, "terminal money mismatch requires closedAt");
        if (mismatch.classification === "unexplained") {
            req(false, "unexplained money mismatch cannot be terminal");
        }
        else {
            req(terminalStatusesByClassification[mismatch.classification].includes(mismatch.status), "terminal status not allowed for this money classification");
        }
        if (mismatch.status === "closed_adjudicated" ||
            mismatch.status === "closed_accepted") {
            req(!!mismatch.ledgerRef, `${mismatch.status} requires ledgerRef`);
        }
        if (mismatch.status === "closed_accepted") {
            req(!!mismatch.reviewedBy, "closed_accepted requires reviewedBy");
            req(hasEvidence("rich_commercial_decision") ||
                hasEvidence("d1_scope_policy") ||
                hasEvidence("source_index_row") ||
                hasEvidence("golden_correction"), "closed_accepted requires durable source or Rich decision evidence");
        }
    }
});
export const MONEY_MISMATCH_LEDGER_SCHEMA_VERSION = 1;
export const MoneyMismatchLedgerSchema = z
    .object({
    ledgerSchemaVersion: z.literal(MONEY_MISMATCH_LEDGER_SCHEMA_VERSION),
    ledgerVersion: z.number().int().nonnegative(),
    benchmarkRunId: IdSchema,
    generatedAt: IsoTimestampSchema,
    mismatches: z.array(MoneyMismatchSchema),
    ledgerHash: NonEmptyStringSchema
})
    .strict();
export const MoneyLedgerStampSchema = z
    .object({
    ledgerSchemaVersion: z.literal(MONEY_MISMATCH_LEDGER_SCHEMA_VERSION),
    ledgerVersion: z.number().int().nonnegative(),
    ledgerHash: NonEmptyStringSchema
})
    .strict();
export const SignedGbpDeltaSchema = z
    .object({
    amount: z.number(),
    currency: QuoteCurrencySchema
})
    .strict();
export const DeliveredQuoteConfidenceSchema = z.enum([
    "none",
    "low",
    "medium",
    "high"
]);
export const DeliveredQuoteScopeHintSchema = z.enum([
    "equipment_supply",
    "grand_total",
    "contract_value",
    "valuation",
    "install_commissioning",
    "commercial_override",
    "unknown"
]);
export const DeliveredQuoteLineFactSchema = z
    .object({
    id: IdSchema,
    label: NonEmptyStringSchema,
    partNo: NonEmptyStringSchema.optional(),
    quantity: z.number().nonnegative().optional(),
    unitPrice: GbpMoneySchema.optional(),
    lineTotal: GbpMoneySchema.optional(),
    sourceRefs: z.array(SourceRefSchema).min(1),
    classificationHint: MoneyMismatchClassificationSchema.optional()
})
    .strict();
export const DeliveredQuoteTotalCandidateSchema = z
    .object({
    id: IdSchema,
    label: NonEmptyStringSchema,
    amountExVat: GbpMoneySchema,
    confidence: DeliveredQuoteConfidenceSchema,
    scopeHint: DeliveredQuoteScopeHintSchema,
    sourceRefs: z.array(SourceRefSchema).min(1)
})
    .strict();
export const DeliveredQuoteFactSchema = z
    .object({
    id: IdSchema,
    sourceIndexLine: z.number().int().positive(),
    name: NonEmptyStringSchema,
    localPath: NonEmptyStringSchema.optional(),
    sourceRefs: z.array(SourceRefSchema).min(1),
    selectedTotal: DeliveredQuoteTotalCandidateSchema.optional(),
    candidateTotals: z.array(DeliveredQuoteTotalCandidateSchema),
    lineItems: z.array(DeliveredQuoteLineFactSchema),
    extractionConfidence: DeliveredQuoteConfidenceSchema,
    extractionNotes: z.array(NonEmptyStringSchema)
})
    .strict();
export const MoneySubtotalDeltaSchema = z
    .object({
    key: NonEmptyStringSchema,
    label: NonEmptyStringSchema,
    generatedAmountExVat: GbpMoneySchema,
    deliveredAmountExVat: GbpMoneySchema.optional(),
    signedDelta: SignedGbpDeltaSchema.optional()
})
    .strict();
export const MoneyProjectScorecardSchema = z
    .object({
    id: IdSchema,
    benchmarkRunId: IdSchema,
    benchmarkCaseId: IdSchema,
    projectName: NonEmptyStringSchema,
    generatedQuoteHash: NonEmptyStringSchema,
    generatedPricingBasis: NonEmptyStringSchema,
    generatedTotalExVat: GbpMoneySchema,
    deliveredTotalExVat: GbpMoneySchema.optional(),
    absoluteDelta: GbpMoneySchema.optional(),
    signedDelta: SignedGbpDeltaSchema.optional(),
    percentageDelta: z.number().optional(),
    percentageDeltaDenominator: z
        .enum(["delivered_total_ex_vat", "not_available"])
        .optional(),
    pricedCoverageRate: z.number().min(0).max(1),
    missingPriceCount: z.number().int().nonnegative(),
    amountAtRiskCount: z.number().int().nonnegative(),
    subtotalDeltasByProductGroup: z.record(QuoteProductGroupSchema, MoneySubtotalDeltaSchema),
    subtotalDeltasBySupplySource: z.record(NonEmptyStringSchema, MoneySubtotalDeltaSchema),
    deliveredQuoteConfidence: DeliveredQuoteConfidenceSchema,
    deliveredQuoteSourceRefs: z.array(SourceRefSchema),
    mismatchIds: z.array(IdSchema),
    materialityThreshold: GbpMoneySchema,
    roundingTolerance: GbpMoneySchema,
    residualAfterClassifiedContributions: GbpMoneySchema,
    explainedStatus: z.enum([
        "explained_closed",
        "open_material_deltas",
        "needs_delivered_quote_source",
        "generated_quote_incomplete"
    ]),
    summary: NonEmptyStringSchema
})
    .strict()
    .superRefine((scorecard, ctx) => {
    if (scorecard.residualAfterClassifiedContributions.amount >
        scorecard.materialityThreshold.amount) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "classified money contributions do not reconcile project delta"
        });
    }
});
export const CorpusSourceCountsSchema = z
    .object({
    total: z.number().int().nonnegative(),
    projectQuote: z.number().int().nonnegative(),
    schedule: z.number().int().nonnegative(),
    apartmentTakeoff: z.number().int().nonnegative(),
    priceList: z.number().int().nonnegative(),
    other: z.number().int().nonnegative()
})
    .strict();
export const MoneyBenchmarkRollupSchema = z
    .object({
    benchmarkRunId: IdSchema,
    generatedAt: IsoTimestampSchema,
    materialityThreshold: GbpMoneySchema,
    roundingTolerance: GbpMoneySchema,
    moneyLedgerStamp: MoneyLedgerStampSchema,
    sourceCounts: CorpusSourceCountsSchema,
    caseCount: z.number().int().nonnegative(),
    comparedCaseCount: z.number().int().nonnegative(),
    materialMismatchCount: z.number().int().nonnegative(),
    openMaterialMismatchCount: z.number().int().nonnegative(),
    unexplainedMaterialMismatchCount: z.number().int().nonnegative(),
    missingPriceMismatchCount: z.number().int().nonnegative(),
    staleLedger: z.boolean(),
    perCase: z.array(z
        .object({
        benchmarkCaseId: IdSchema,
        generatedTotalExVat: GbpMoneySchema,
        deliveredTotalExVat: GbpMoneySchema.optional(),
        signedDelta: SignedGbpDeltaSchema.optional(),
        explainedStatus: NonEmptyStringSchema,
        materialMismatchCount: z.number().int().nonnegative(),
        openMaterialMismatchCount: z.number().int().nonnegative()
    })
        .strict()),
    passed: z.boolean(),
    summary: NonEmptyStringSchema
})
    .strict()
    .superRefine((rollup, ctx) => {
    const req = (condition, message) => {
        if (!condition) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message });
        }
    };
    if (rollup.passed) {
        req(!rollup.staleLedger, "passed rollup cannot have stale money ledger");
        req(rollup.openMaterialMismatchCount === 0, "passed rollup cannot have open material money mismatches");
        req(rollup.unexplainedMaterialMismatchCount === 0, "passed rollup cannot have unexplained material money mismatches");
        req(rollup.missingPriceMismatchCount === 0, "passed rollup cannot have missing-price blockers");
    }
});
//# sourceMappingURL=moneyBenchmark.js.map